import { useState } from "react";
import * as XLSX from "xlsx";

import { faro } from "@grafana/faro-react";
import { useToast } from "@vega-core/components/Toast/useToast";
import { UTF_8_BYTE_ORDER_MARK } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { downloadBlobAsFile } from "@vega-core/utils/download.helpers";

export type UseTableExportOptions<TData> = {
  data: TData[];
  columns: ExportColumnConfig<TData>[];
  filename: string;
  groupedHeaders?: ExportGroupedHeader[];
};

export type UseTableExportReturn = {
  exportToCsv: () => void;
  exportToExcel: () => void;
  isExporting: boolean;
};

export type ExportColumnConfig<TData> = {
  header: string;
  accessor: (row: TData) => boolean | number | string | null | undefined;
};

export type ExportGroupedHeader = {
  label: string;
  colSpan: number;
};

type ExportCellValue = boolean | number | string | null | undefined;

/**
 * Formats a single cell value for export.
 * Handles null/undefined as empty string, booleans as yes/no labels.
 */
const formatExportValue = (
  value: ExportCellValue,
  yesLabel: string,
  noLabel: string,
): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? yesLabel : noLabel;
  }

  return String(value);
};

/**
 * Transforms raw data array into headers and formatted string rows.
 */
const transformDataToRows = <TData>(
  data: TData[],
  columns: ExportColumnConfig<TData>[],
  formatValueFn: (value: ExportCellValue) => string,
): { headers: string[]; rows: string[][] } => {
  const headers = columns.map((col) => col.header);
  const rows = data.map((row) =>
    columns.map((col) => {
      const value = col.accessor(row);

      return formatValueFn(value);
    }),
  );

  return { headers, rows };
};

/**
 * Builds a grouped header row array from grouped header configuration.
 * Each group label is followed by empty cells to fill the column span.
 */
const buildGroupedHeaderRow = (
  groupedHeaders: ExportGroupedHeader[],
  transformCell?: (cell: string) => string,
): string[] =>
  groupedHeaders.flatMap((group) => {
    const cellValue = transformCell ? transformCell(group.label) : group.label;

    return [cellValue, ...Array(group.colSpan - 1).fill("")];
  });

/**
 * Escapes a single cell for CSV format.
 * Wraps in quotes and escapes internal quotes if cell contains comma, quote, or newline.
 */
const escapeCsvCell = (cell: string): string => {
  const cellStr = String(cell);

  if (
    cellStr.includes(",") ||
    cellStr.includes('"') ||
    cellStr.includes("\n")
  ) {
    return `"${cellStr.replace(/"/g, '""')}"`;
  }

  return cellStr;
};

/**
 * Builds the complete data matrix for export (grouped headers + headers + rows).
 */
const buildExportDataMatrix = (
  headers: string[],
  rows: string[][],
  groupedHeaders?: ExportGroupedHeader[],
): string[][] => {
  const matrix: string[][] = [];

  if (groupedHeaders?.length) {
    matrix.push(buildGroupedHeaderRow(groupedHeaders));
  }

  matrix.push(headers, ...rows);

  return matrix;
};

/**
 * Creates a CSV Blob from headers, rows, and optional grouped headers.
 */
const createCsvBlob = (
  headers: string[],
  rows: string[][],
  groupedHeaders?: ExportGroupedHeader[],
): Blob => {
  const matrix = buildExportDataMatrix(headers, rows, groupedHeaders);
  const csvContent =
    UTF_8_BYTE_ORDER_MARK +
    matrix.map((row) => row.map(escapeCsvCell).join(",")).join("\n");

  return new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
};

/**
 * Builds Excel merge configuration for grouped headers.
 * Only creates merge ranges for groups with colSpan > 1.
 */
const buildExcelMerges = (
  groupedHeaders: ExportGroupedHeader[],
): XLSX.Range[] => {
  const merges: XLSX.Range[] = [];
  let colIndex = 0;

  groupedHeaders.forEach((group) => {
    if (group.colSpan > 1) {
      merges.push({
        s: { r: 0, c: colIndex },
        e: { r: 0, c: colIndex + group.colSpan - 1 },
      });
    }
    colIndex += group.colSpan;
  });

  return merges;
};

/**
 * Creates an Excel Blob from headers, rows, and optional grouped headers.
 * Applies cell merging for grouped headers when provided.
 */
const createExcelBlob = (
  headers: string[],
  rows: string[][],
  groupedHeaders?: ExportGroupedHeader[],
): Blob => {
  const worksheetData = buildExportDataMatrix(headers, rows, groupedHeaders);
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  if (groupedHeaders?.length) {
    worksheet["!merges"] = buildExcelMerges(groupedHeaders);
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  return new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export function useTableExport<TData>({
  data,
  columns,
  filename,
  groupedHeaders,
}: UseTableExportOptions<TData>): UseTableExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const showToast = useToast();

  const executeExport = async (exportType: "csv" | "excel") => {
    try {
      setIsExporting(true);

      const formatValueFn = (
        value: boolean | number | string | null | undefined,
      ) => formatExportValue(value, m.common_yes(), m.common_no());

      const { headers, rows } = transformDataToRows(
        data,
        columns,
        formatValueFn,
      );

      const blob =
        exportType === "csv"
          ? createCsvBlob(headers, rows, groupedHeaders)
          : createExcelBlob(headers, rows, groupedHeaders);

      const extension = exportType === "csv" ? "csv" : "xlsx";
      downloadBlobAsFile(blob, `${filename}.${extension}`);
    } catch (err) {
      faro.api.pushError(err as Error, {
        type: "export_failed",
        context: {
          exportType,
          filename,
          timestamp: new Date().toISOString(),
        },
      });
      showToast({
        title: m.common_error_exportError(),
        description: m.errors_anUnexpectedErrorOccur(),
        type: "error",
        toastId: "export-error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportToCsv = () => {
    executeExport("csv");
  };

  const exportToExcel = () => {
    executeExport("excel");
  };

  return {
    exportToCsv,
    exportToExcel,
    isExporting,
  };
}
